import type { User } from '@supabase/supabase-js'
import type {
  AuthErrorResponse,
  AuthResult,
  LoginResponse,
  RegisterResponse,
  UserData,
  UserMetadata,
} from '#shared/types/auth'
import { computed, watch } from 'vue'
import { logApiError } from '~/utils/logger'

/**
 * Auth State Management
 * Centralized reactive state for authentication
 * Note: User state is managed by @nuxtjs/supabase via useSupabaseUser()
 */
export const useAuthState = () => {
  const user = useState<User | null>('auth-user', () => null)
  const userData = useState<UserData | null>('auth-user-data', () => null)

  // Initialize loading to false to prevent SSR hydration mismatches
  // It will be set to true only during actual auth operations
  const loading = useState<boolean>('auth-loading', () => false)

  return {
    user,
    userData,
    loading,
  }
}

/**
 * Pure Supabase Authentication Composable
 * Single source of truth for all auth operations
 * Uses @nuxtjs/supabase module for auto-imported client
 */
export const useSupabaseAuth = () => {
  const supabase = useSupabaseClient() // Auto-imported by @nuxtjs/supabase
  const supabaseUser = useSupabaseUser() // Auto-imported by @nuxtjs/supabase
  const authState = useAuthState()

  // Sync module's user with our state
  watch(
    supabaseUser,
    newUser => {
      authState.user.value = newUser as User | null
    },
    { immediate: true },
  )

  // Computed properties for convenience
  const isAuthenticated = computed(() => !!supabaseUser.value)
  const currentUser = computed(() => supabaseUser.value)
  const currentUserData = computed(() => authState.userData.value)

  // Track if a sync is in progress to prevent duplicate calls
  let syncInProgress = false
  let syncPromise: Promise<void> | null = null

  /**
   * Sync user data and permissions from server
   * This fetches profile data and CASL ability rules
   * Includes retry logic for race conditions after sign-in
   */
  const syncUserData = async (
    retryCount: number = 0,
    maxRetries: number = 3,
  ): Promise<void> => {
    // Don't sync if there's no authenticated user
    if (!supabaseUser.value) {
      authState.userData.value = null

      return
    }

    // If a sync is already in progress, return the existing promise
    if (syncInProgress && syncPromise && retryCount === 0)
      return syncPromise

    // Create new sync promise
    syncPromise = (async () => {
      syncInProgress = true

      try {
        // Get the current session to extract the access token
        // This is more reliable than cookies in Nuxt client-side API calls
        const {
          data: { session },
        } = await supabase.auth.getSession()

        if (!session?.access_token)
          throw new Error('No access token available in session')

        // Fetch user profile and permissions from server-validated endpoint
        // Send the access token as Authorization header
        const userData = await $fetch('/api/me', {
          headers: {
            Authorization: `Bearer ${session.access_token}`,
          },
        })

        // Only set userData if it's not an error response
        if (userData && !('error' in userData))
          authState.userData.value = userData as unknown as UserData
      }
      catch (error) {
        const authError = error as AuthErrorResponse

        // Don't retry on rate limit errors (429) - just fail and let user try again later
        if (authError.statusCode === 429) {
          logApiError('/api/me', new Error('Rate limit exceeded when syncing user data'), {
            statusCode: 429,
          })
          authState.userData.value = null
          syncInProgress = false

          return
        }

        // Check if it's a 401 error (session not yet propagated to server)
        // This can happen due to race condition after sign-in
        // But only retry if we still have a supabase user (not logged out)
        if (
          authError.statusCode === 401
          && retryCount < maxRetries
          && supabaseUser.value
        ) {
          // Use exponential backoff: 1s, 2s, 4s
          await new Promise(resolve =>
            setTimeout(resolve, 1000 * 2 ** retryCount),
          )
          syncInProgress = false

          return syncUserData(retryCount + 1, maxRetries)
        }

        // Don't log 401 errors during initial load when there's no session
        // This is expected behavior when user is not logged in
        if (authError.statusCode !== 401)
          logApiError('/api/me', error, { statusCode: authError.statusCode })

        // On error, clear user data but keep session
        // User will be prompted to re-authenticate
        authState.userData.value = null
      }
      finally {
        syncInProgress = false
      }
    })()

    return syncPromise
  }

  /**
   * Initialize auth state from existing Supabase session
   * Module handles session initialization, we just sync user data
   */
  const initializeAuth = async () => {
    authState.loading.value = true

    try {
      // Only sync if we have an authenticated user from the module
      if (supabaseUser.value) {
        // Fetch user data and permissions from server
        await syncUserData()
      }
      else {
        // No user - ensure user data is cleared
        authState.userData.value = null
      }
    }
    catch (error) {
      logApiError('auth/initialize', error)
      authState.userData.value = null
    }
    finally {
      authState.loading.value = false
    }
  }

  /**
   * Sign in with email/password
   * Uses rate-limited API endpoint
   */
  const signInWithEmail = async (
    email: string,
    password: string,
  ): Promise<AuthResult<LoginResponse>> => {
    authState.loading.value = true

    try {
      // Call rate-limited login endpoint
      const response = await $fetch<LoginResponse>('/api/auth/login', {
        method: 'POST',
        body: { email, password },
      })

      if (response.success && response.session) {
        // Explicitly set the session on the client
        // This ensures the session cookies are properly set
        await supabase.auth.setSession({
          access_token: response.session.access_token,
          refresh_token: response.session.refresh_token,
        })

        // Wait a moment for session to propagate
        await new Promise(resolve => setTimeout(resolve, 100))

        // Fetch user data and permissions
        await syncUserData()

        return { data: response, error: null }
      }

      return { data: null, error: new Error('Login failed') }
    }
    catch (error) {
      const authError = error as AuthErrorResponse

      // Handle rate limit errors
      if (authError.statusCode === 429) {
        return {
          data: null,
          error: {
            message:
              authError.message
              || 'Too many login attempts. Please try again later.',
            statusCode: 429,
            retryAfter: authError.retryAfter,
          },
        }
      }

      // Handle other errors
      const err = error as {
        data?: { message?: string }
        statusMessage?: string
        statusCode?: number
      }

      return {
        data: null,
        error: {
          message: err.data?.message || err.statusMessage || 'Login failed',
          statusCode: err.statusCode || 500,
        },
      }
    }
    finally {
      authState.loading.value = false
    }
  }

  /**
   * Sign up with email/password
   * Uses rate-limited API endpoint
   */
  const signUpWithEmail = async (
    email: string,
    password: string,
    fullName: string,
    metadata?: UserMetadata,
    profileData?: { phone?: string, address?: string, postalCode?: string, city?: string, country?: string },
  ): Promise<AuthResult<RegisterResponse>> => {
    authState.loading.value = true

    try {
      // Call rate-limited register endpoint
      const response = await $fetch<RegisterResponse>('/api/auth/register', {
        method: 'POST',
        body: {
          email,
          password,
          full_name: fullName,
          phone: profileData?.phone || null,
          address: profileData?.address || null,
          postal_code: profileData?.postalCode || null,
          city: profileData?.city || null,
          country: profileData?.country || null,
          metadata,
        },
      })

      if (response.success) {
        // Session may be null if email confirmation is required
        if (response.session) {
          // Explicitly set the session on the client
          await supabase.auth.setSession({
            access_token: response.session.access_token,
            refresh_token: response.session.refresh_token,
          })

          // Wait a moment for session to propagate
          await new Promise(resolve => setTimeout(resolve, 100))

          await syncUserData()
        }

        return { data: response, error: null }
      }

      return { data: null, error: new Error('Registratie mislukt') }
    }
    catch (error: unknown) {
      // Handle FetchError from $fetch
      const fetchError = error as {
        statusCode?: number
        statusMessage?: string
        message?: string
        data?: {
          statusMessage?: string
          data?: {
            code?: string
            field?: string
          }
        }
      }

      const statusCode = fetchError.statusCode || 400

      // Handle rate limit errors
      if (statusCode === 429) {
        return {
          data: null,
          error: {
            message:
              fetchError.message
              || 'Te veel registratiepogingen. Probeer het later opnieuw.',
            statusCode: 429,
            retryAfter: (fetchError as { retryAfter?: number }).retryAfter,
          },
        }
      }

      // Handle duplicate email
      if (statusCode === 409) {
        return {
          data: null,
          error: {
            message:
              fetchError.message || 'Er bestaat al een account met dit e-mailadres',
            statusCode: 409,
          },
        }
      }

      // Handle password compromised/pwned errors
      if (statusCode === 422) {
        const errorMessage
          = fetchError.data?.statusMessage
            || fetchError.statusMessage
            || fetchError.message
            || 'Het wachtwoord voldoet niet aan de veiligheidseisen'

        const errorCode = fetchError.data?.data?.code || 'password_error'

        return {
          data: null,
          error: {
            message: errorMessage,
            statusCode: 422,
            code: errorCode,
          } as AuthErrorResponse & { code?: string },
        }
      }

      // For other errors, try to extract the message properly
      const errorMessage
        = fetchError.data?.statusMessage
          || fetchError.statusMessage
          || fetchError.message
          || 'Registratie mislukt'

      return {
        data: null,
        error: {
          message: errorMessage,
          statusCode,
        },
      }
    }
    finally {
      authState.loading.value = false
    }
  }

  /**
   * Sign in with OAuth provider
   */
  const signInWithOAuth = async (
    provider: 'google' | 'azure' | 'github',
    returnPath?: string,
  ) => {
    const config = useRuntimeConfig()

    // Always use window.location.origin on client side for correct environment detection
    // This ensures localhost is used during development and production URL in production
    const baseUrl = import.meta.client
      ? window.location.origin
      : config.public.siteUrl

    // Build callback URL with redirect parameter if provided
    let callbackUrl = `${baseUrl}/auth/callback`
    if (returnPath) {
      callbackUrl += `?redirect=${encodeURIComponent(returnPath)}`

      // Store in localStorage as backup
      if (import.meta.client)
        localStorage.setItem('auth_redirect', returnPath)
    }

    const { data, error } = await supabase.auth.signInWithOAuth({
      provider,
      options: {
        redirectTo: callbackUrl,
        ...(provider === 'azure' && { scopes: 'email profile openid' }),
        ...(provider === 'google' && {
          queryParams: { prompt: 'select_account' },
        }),
      },
    })

    return { data, error }
  }

  // Convenience methods for specific providers
  const signInWithGoogle = (returnPath?: string) =>
    signInWithOAuth('google', returnPath)

  const signInWithMicrosoft = (returnPath?: string) =>
    signInWithOAuth('azure', returnPath)

  const signInWithGitHub = (returnPath?: string) =>
    signInWithOAuth('github', returnPath)

  /**
   * Reset password
   * Uses rate-limited API endpoint
   */
  const resetPassword = async (email: string) => {
    try {
      const response = await $fetch('/api/auth/forgot-password', {
        method: 'POST',
        body: { email },
      })

      return { data: response, error: null }
    }
    catch (error) {
      const authError = error as AuthErrorResponse

      // Handle rate limit errors
      if (authError.statusCode === 429) {
        return {
          data: null,
          error: {
            message:
              authError.message
              || 'Too many password reset attempts. Please try again later.',
            statusCode: 429,
            retryAfter: authError.retryAfter,
          },
        }
      }

      return { data: null, error: error as Error }
    }
  }

  /**
   * Sign in with magic link (passwordless)
   * Uses rate-limited API endpoint
   */
  const signInWithMagicLink = async (email: string, redirectTo?: string) => {
    try {
      const response = await $fetch('/api/auth/magic-link', {
        method: 'POST',
        body: { email, redirectTo },
      })

      return { data: response, error: null }
    }
    catch (error) {
      const authError = error as AuthErrorResponse

      // Handle rate limit errors
      if (authError.statusCode === 429) {
        return {
          data: null,
          error: {
            message:
              authError.message
              || 'Too many magic link requests. Please try again later.',
            statusCode: 429,
            retryAfter: authError.retryAfter,
          },
        }
      }

      return { data: null, error: error as Error }
    }
  }

  /**
   * Update password
   * Uses rate-limited API endpoint
   */
  const updatePassword = async (newPassword: string) => {
    try {
      const response = await $fetch('/api/auth/reset-password', {
        method: 'POST',
        body: { password: newPassword },
      })

      return { data: response, error: null }
    }
    catch (error) {
      const authError = error as AuthErrorResponse

      // Handle rate limit errors
      if (authError.statusCode === 429) {
        return {
          data: null,
          error: {
            message:
              authError.message
              || 'Too many password update attempts. Please try again later.',
            statusCode: 429,
            retryAfter: authError.retryAfter,
          },
        }
      }

      // Handle 401: session expired or reset link already used
      if (authError.statusCode === 401) {
        return {
          data: null,
          error: {
            message: 'Je sessie is verlopen of de resetlink is al gebruikt. Vraag een nieuwe resetlink aan.',
            statusCode: 401,
          },
        }
      }

      return { data: null, error: error as Error }
    }
  }

  /**
   * Sign out
   */
  const signOut = async () => {
    authState.loading.value = true

    try {
      // Clear state BEFORE signing out to prevent auth state change handler from failing
      authState.user.value = null
      authState.userData.value = null

      const { error } = await supabase.auth.signOut()

      return { error }
    }
    finally {
      authState.loading.value = false
    }
  }

  return {
    // State
    ...authState,
    isAuthenticated,
    currentUser,
    currentUserData,

    // Methods
    initializeAuth,
    syncUserData,
    signInWithEmail,
    signUpWithEmail,
    signInWithOAuth,
    signInWithGoogle,
    signInWithMicrosoft,
    signInWithGitHub,
    signInWithMagicLink,
    resetPassword,
    updatePassword,
    signOut,

    // Direct access to Supabase client if needed
    supabase,
  }
}
